import type { JSONRecord } from "archipelago.js";

import { AugmentationName, CompletedProgramName } from "@enums";
import type { FactionName } from "@enums";
import { Augmentations } from "../Augmentation/Augmentations";
import type { AchievementId } from "../Achievements/Types";

/**
 * The game name this client authenticates as. Must match the "game" field of the Bitburner apworld.
 *
 * The server address, port, slot name and password are player-configurable in
 * Settings (Options -> Archipelago).
 */
export const AP_GAME = "Bitburner";

/**
 * Resolves an Archipelago item name to the augmentation it grants.
 *
 * The apworld's item pool is every augmentation in the game, named exactly as Bitburner names them,
 * so this is an identity mapping. It is resolved against the game's own augmentation registry rather
 * than a copied table, which means the two cannot drift as augmentations are added or renamed.
 */
export function augmentationForItem(itemName: string): AugmentationName | undefined {
  // hasOwnProperty rather than `in`: the latter walks the prototype chain, so an item literally
  // named "constructor" or "toString" would resolve to a non-augmentation.
  return Object.prototype.hasOwnProperty.call(Augmentations, itemName) ? (itemName as AugmentationName) : undefined;
}

/**
 * Resolves an Archipelago item name to the program it grants.
 *
 * The apworld names its program items after Bitburner's own program filenames, so this is an identity
 * mapping, resolved against the game's enum rather than a copied list for the same reason as
 * augmentationForItem.
 *
 * Deliberately accepts any CompletedProgramName, not just the five port openers the pool currently
 * holds. An apworld that later adds Formulas.exe as an item then works without a client change, and
 * anything genuinely unknown still falls through to the unmapped-item warning.
 */
export function programForItem(itemName: string): CompletedProgramName | undefined {
  return PROGRAM_NAMES.has(itemName) ? (itemName as CompletedProgramName) : undefined;
}

const PROGRAM_NAMES = new Set<string>(Object.values(CompletedProgramName));

/**
 * Bitburner achievement -> the Archipelago location it checks.
 *
 * Excluded are the secret achievements, the source file achievements for other BitNodes, and the
 * challenge-run achievements. The names must match the apworld's ACHIEVEMENT_LOCATIONS exactly.
 */
export const ACHIEVEMENT_TO_LOCATION: Partial<Record<AchievementId, string>> = {
  CYBERSEC: "Join CyberSec",
  NITESEC: "Join NiteSec",
  THE_BLACK_HAND: "Join The Black Hand",
  BITRUNNERS: "Join the BitRunners",
  DAEDALUS: "Join Daedalus",
  THE_COVENANT: "Join The Covenant",
  ILLUMINATI: "Join the Illuminati",
  "BRUTESSH.EXE": "Acquire BruteSSH.exe",
  "FTPCRACK.EXE": "Acquire FTPCrack.exe",
  "RELAYSMTP.EXE": "Acquire relaySMTP.exe",
  "HTTPWORM.EXE": "Acquire HTTPWorm.exe",
  "SQLINJECT.EXE": "Acquire SQLInject.exe",
  "FORMULAS.EXE": "Acquire Formulas.exe",
  "SF1.1": "Acquire Source Genesis",
  MONEY_1Q: "Hold $1Q",
  INSTALL_1: "Install Your First Augmentation",
  INSTALL_100: "Install 100 Augmentations",
  QUEUE_40: "Queue 40 Augmentations",
  HACKING_100000: "Reach 100,000 Hacking Skill",
  COMBAT_3000: "Reach 3000 in All Combat Stats",
  NEUROFLUX_255: "Install NeuroFlux Governor Level 255",
  NS2: "Write a .js Script",
  FROZE: "Freeze the Game With an Infinite Loop",
  RUNNING_SCRIPTS_1000: "Run 1000 Scripts Simultaneously",
  DRAIN_SERVER: "Drain a Server of All Its Money",
  MAX_RAM: "Maximize Home Computer RAM",
  MAX_CORES: "Maximize Home Computer Cores",
  SCRIPTS_30: "Keep 30 Scripts on Home",
  STOCK_1q: "Make $1q on the Stock Market",
  DISCOUNT: "Earn the Powerhouse Gym Discount",
  SCRIPT_32GB: "Write a 32GB Script",
  FIRST_HACKNET_NODE: "Purchase Your First Hacknet Node",
  "30_HACKNET_NODE": "Own 30 Hacknet Nodes",
  MAX_HACKNET_NODE: "Maximize a Hacknet Node",
  HACKNET_NODE_10M: "Earn $10m From Hacknet Nodes",
  REPUTATION_10M: "Reach 10m Faction Reputation",
  DONATION: "Unlock Faction Donations",
  TRAVEL: "Travel to Another City",
  WORKOUT: "Work Out at a Gym",
  TOR: "Purchase the TOR Router",
  HOSPITALIZED: "Get Hospitalized",
  GANG: "Form a Gang",
  FULL_GANG: "Recruit All Gang Members",
  GANG_TERRITORY: "Control All Gang Territory",
  GANG_MEMBER_POWER: "Train a Gang Member to 10,000 in a Skill",
  CORPORATION: "Create a Corporation",
  CORPORATION_BRIBE: "Lower Corporation Tribute by Lobbying",
  CORPORATION_PROD_1000: "Reach a 1000x Division Production Multiplier",
  CORPORATION_EMPLOYEE_3000: "Employ 3000 in a Division",
  CORPORATION_REAL_ESTATE: "Expand Into Real Estate",
  INTELLIGENCE_255: "Reach Intelligence 255",
  BLADEBURNER_DIVISION: "Join the Bladeburner Division",
  BLADEBURNER_OVERCLOCK: "Max Out Bladeburner Overclock",
  BLADEBURNER_UNSPENT_100000: "Bank 100,000 Bladeburner Skill Points",
  "4S": "Purchase 4S Market Data",
  FIRST_HACKNET_SERVER: "Purchase Your First Hacknet Server",
  ALL_HACKNET_SERVER: "Buy All Hacknet Servers",
  MAX_HACKNET_SERVER: "Maximize a Hacknet Server",
  HACKNET_SERVER_1B: "Earn $1b From Hacknet Servers",
  MAX_CACHE: "Cap Your Hashes",
  SLEEVE_8: "Acquire All 8 Sleeves",
  INDECISIVE: "Spend an Hour in the BitVerse",
  FAST_BN: "Destroy a BitNode in Under 2 Days",
  BN_DESTROYER: "Reach SF x.3 in Every BitNode",
  IPVGO_ANTICHEAT: "Get Ejected From an IPvGO Subnet",
  IPVGO_WINNING_STREAK: "Win 10 IPvGO Games in a Row",
  DARKNET_BACKDOOR: "Backdoor 50 Darknet Servers at Once",
  DARKNET_DEPTHS: "Install the Augment From the Deepest Server",
};

/**
 * The Archipelago location for backdooring a server.
 *
 * Derived rather than tabulated, for the same reason as augmentationForItem. Servers the apworld
 * does not know about -- purchased servers, home -- simply resolve to a name with no location id,
 * which the check path skips.
 */
export function backdoorLocation(hostname: string): string {
  return `Backdoor ${hostname}`;
}

/**
 * The Archipelago location for buying a program from the darkweb.
 *
 * Derived rather than tabulated, for the same reason as backdoorLocation. If this fork adds a darkweb
 * item the apworld does not know about, it resolves to a name with no location id and the check path
 * skips it, rather than the two lists silently disagreeing.
 *
 * This is the classic TOR darkweb reached with the 'buy' command, not the separate DarkNet system.
 */
export function darkwebPurchaseLocation(program: CompletedProgramName): string {
  return `Purchase ${program}`;
}

/**
 * The Archipelago location for writing a program yourself.
 *
 * Derived rather than tabulated, for the same reason as backdoorLocation. The apworld only defines
 * these for the 10 programs that are both creatable and purchasable, so NUKE.exe and b1t_flum3.exe
 * resolve to a name with no id and are dropped -- which is deliberate. NUKE is owned from the start,
 * and b1t_flum3 needs knowAboutBitverse(), false for a whole first BitNode 1 run, so either would be
 * a location the player could not reach.
 */
export function programCreationLocation(program: CompletedProgramName): string {
  return `Create ${program}`;
}

/**
 * The Archipelago location for joining a faction.
 *
 * Derived rather than tabulated, for the same reason as backdoorLocation. The apworld names these
 * after Bitburner's own faction names, so this is an identity mapping over FactionName.
 *
 * Seven factions are deliberately absent from the apworld's list because joining them is already an
 * achievement, and two locations for one action would be worse than an inconsistent naming scheme.
 * Five of those seven (CyberSec, NiteSec, The Black Hand, Daedalus, The Covenant) produce exactly the
 * name their achievement location already uses, so they harmlessly duplicate a check that is
 * idempotent server-side. The other two produce "Join BitRunners" and "Join Illuminati", which no
 * location is called -- their achievements say "Join the BitRunners" and "Join the Illuminati" -- so
 * they resolve to no id and are dropped, exactly as purchased servers are in backdoorLocation.
 *
 * That drop is why no exclusion list is needed here. Adding one would mean this client had to know
 * which locations the apworld omits, which is precisely the coupling the derived names avoid.
 */
export function factionJoinLocation(faction: FactionName): string {
  return `Join ${faction}`;
}

/**
 * The achievement that completes the default goal.
 *
 * SF1.1 is "Source Genesis", earned by destroying BitNode 1. It is also a location in its own right,
 * so finishing a run both sends a check and completes the goal.
 */
export const GOAL_ACHIEVEMENT: AchievementId = "SF1.1";

/** The goal the player chose, as sent in slot data. Mirrors the apworld's Goal option. */
export const Goal = {
  SourceGenesis: 0,
} as const;

export type GoalValue = (typeof Goal)[keyof typeof Goal];

/**
 * Reads the chosen goal out of the slot data the apworld sends on connection.
 *
 * Source Genesis is currently the only goal, so this always returns it. It stays a function because
 * slot data comes from whatever apworld version generated the seed, which need not match this
 * client: a seed built by a newer apworld may name a goal this client does not know, and falling
 * back is what keeps that connectable rather than throwing.
 */
export function parseGoal(slotData: JSONRecord): GoalValue {
  void slotData;
  return Goal.SourceGenesis;
}
